import { defaultServerDateTimeFormat } from 'assets/utils/data/Date';
import { roundToStep } from 'assets/utils/data/Number';
import dayjs from 'dayjs';
import Currency from 'models/enums/Currency';
import FundingSourceCode from 'models/enums/FundingSourceCode';
import Model from 'models/Model';
import ContractRefundPayee from 'models/sales/contractRefundPayee/Model.ContractRefundPayee';
import ContractRefund from '../contractRefund/Model.ContractRefund';
import contractRefundDealerPaymentControlListApi from './list/Api.ContractRefundDealerPaymentControl.List';

export type ContractRefundAccountingItemRequest = Model.IContractRefundPaymentItem | Model.IContractRefundInvoice;

export default class ContractRefundDealerPaymentControl
  extends Model<Model.IContractRefundDealerPaymentControl>({ icon: 'fas-cloud' })
  implements Model.Instance<Model.IContractRefundDealerPaymentControl>
{
  id: number;
  contractRefundId: number;
  contractRefund: ContractRefund;

  contractRefundPayeeId: number;
  contractRefundPayee?: ContractRefundPayee;

  allocatedAmountCAD: number;
  fundingSourceCode?: Model.Enum.FundingSourceCode;
  responsibilityTypeCode?: Model.Enum.ResponsibilityTypeCode;
  paymentPurposeCode?: Model.Enum.PaymentPurposeCode;
  accountingItemTypeCode?: Model.Enum.AccountingItemTypeCode;
  isExcluded?: boolean;

  constructor(
    data:
      | Partial<Model.IContractRefundDealerPaymentControl>
      | Utils.FormData<Model.IContractRefundDealerPaymentControl>,
    language?: Locale.Languages
  ) {
    super(
      {
        contractRefundPayee: ContractRefundPayee,
        contractRefund: ContractRefund,
      },
      language
    );
    this.update(data);
  }

  get displayInfo(): Model.DisplayInfo {
    return {
      id: this.id,
      title: this.id?.toString(),
    };
  }

  public toAccountingItem<T extends ContractRefundAccountingItemRequest>(contract: Model.IContract): T {
    const isDealerProfitFunded =
      this.fundingSourceCode === FundingSourceCode.DEALER_PROFIT ||
      this.fundingSourceCode === FundingSourceCode.ASC_DEALER_PROFIT;
    const [gstHstTax, provincialSalesTax] = isDealerProfitFunded
      ? [contract.dealerProfitGstHst, contract.dealerProfitProvincialSalesTax]
      : [contract.dealerCostGstHst, contract.dealerCostProvincialSalesTax];

    const rawAmount = this.allocatedAmountCAD / (1 + gstHstTax + provincialSalesTax);
    const gstHstAmount = roundToStep(rawAmount * gstHstTax, 0.01);
    const provincialSalesTaxAmount = roundToStep(rawAmount * provincialSalesTax, 0.01);

    return {
      contractRefundDealerPaymentControlId: this.id,
      contractRefundPayeeId: this.contractRefundPayeeId,
      paymentMethodCode: this.contractRefundPayee?.paymentMethodCodeWitFallback,
      createdAtLocal: dayjs().format(defaultServerDateTimeFormat),
      paymentRequestedAtUtc: dayjs().format(defaultServerDateTimeFormat),
      gstHstAmount: gstHstAmount,
      provincialSalesTaxAmount: provincialSalesTaxAmount,
      totalAmountBeforeTax: roundToStep(this.allocatedAmountCAD - gstHstAmount - provincialSalesTaxAmount, 0.01),
      totalAmount: this.allocatedAmountCAD,
      currency: Currency.CAD,
      isExcluded: !!this.isExcluded,
    } as T;
  }

  static async list(body?: Api.Sales.ContractRefundDealerPaymentControl.List.IRequest) {
    const { payload, ...rest } = await contractRefundDealerPaymentControlListApi(body);
    return {
      ...rest,
      payload: payload && {
        data: payload.data?.map((it) => new ContractRefundDealerPaymentControl(it)),
        count: payload.count,
      },
    };
  }
}
